import React from 'react'
import { Teacher } from '../../providers/context'
import { makeRequest, showAxiosErrorAsToast } from '../../request'
import { Divider } from '../../components/simple/divider'
import { Button } from '../../components/simple/button'
import { IconCircle, IconEdit, IconSignIn, IconTimes } from '../../icons/library'
import { formatPhoneNumberForEditing } from '../../utils'
import { Badge, BadgeStyle } from '../../components/simple/badge'
import { EditResponse, LanguageEdit, showEditErrors } from '../modals/editUser'
import { LabeledCheckbox } from '../../components/form/checkbox'
import { EmailEdit, EmailRow, GenericContact, LanguageRow, PhoneEdit, PhoneRow, UsageRow } from './contactComponents'
import { StudentContact } from './familyContactData'
import { getComposeLink, getDirectLink, URLAction } from '../../providers/URLParams'
import { useLanguages } from '../../providers/languages'
import { SmartLink } from '../../components/simple/link'
import { TextInput } from '../../components/form/textInput'

export const ContactGuardian: React.FC<{
  guardian: StudentContact['guardians'][0]
  student: StudentContact
  user: Teacher
  reloadContacts: () => void
}> = ({ guardian, user, reloadContacts, student }) => {
  const languages = useLanguages()

  const [editing, setEditing] = React.useState(false)

  const [loading, setLoading] = React.useState(false)
  const [language, setLanguage] = React.useState(guardian.language)
  const [spokenLanguage, setSpokenLanguage] = React.useState(guardian.spokenLanguage)
  const [email, setEmail] = React.useState(guardian.emailData.email)
  const [emailInvalid, setEmailInvalid] = React.useState(false)
  const [receiveEmails, setReceiveEmails] = React.useState(guardian.emailData.receiveEmails)
  const [phoneNumber, setPhoneNumber] = React.useState(formatPhoneNumberForEditing(guardian.phoneData.phoneNumber))
  const [phoneNumberInvalid, setPhoneNumberInvalid] = React.useState(false)
  const [receiveTexts, setReceiveTexts] = React.useState(guardian.phoneData.receiveTexts)
  const [textAsWhatsApp, setTextAsWhatsApp] = React.useState(
    guardian.phoneData.textAsWhatsApp && guardian.phoneData.receiveTexts
  )
  const [sendTextToSpeech, setSendTextToSpeech] = React.useState(guardian.phoneData.sendTextToSpeech)
  const [receiveCalls, setReceiveCalls] = React.useState(guardian.phoneData.receiveCalls)
  const [sendAttendanceEmails, setSendAttendanceEmails] = React.useState(guardian.attendance?.sendEmails ?? false)
  const [sendAttendanceTexts, setSendAttendanceTexts] = React.useState(guardian.attendance?.sendTexts ?? false)
  const [preferredName, setPreferredName] = React.useState(guardian.preferredName || '')
  const resetEdit = () => {
    setLanguage(guardian.language)
    setSpokenLanguage(guardian.spokenLanguage)
    setEmail(guardian.emailData.email)
    setReceiveEmails(guardian.emailData.receiveEmails)
    setPhoneNumber(formatPhoneNumberForEditing(guardian.phoneData.phoneNumber))
    setReceiveTexts(guardian.phoneData.receiveTexts)
    setTextAsWhatsApp(guardian.phoneData.textAsWhatsApp)
    setSendTextToSpeech(guardian.phoneData.sendTextToSpeech)
    setReceiveCalls(guardian.phoneData.receiveCalls)
    setSendAttendanceEmails(guardian.attendance?.sendEmails ?? false)
    setSendAttendanceTexts(guardian.attendance?.sendTexts ?? false)
    setPreferredName(guardian.preferredName || '')
  }
  React.useEffect(resetEdit, [guardian])

  const edit = () => {
    resetEdit()
    setEditing(true)
  }

  const saveEdit = async () => {
    setLoading(true)
    setEmailInvalid(false)
    setPhoneNumberInvalid(false)
    try {
      const response = await makeRequest<EditResponse>('/frontend_api/editUser', {
        id: guardian.id,
        language: guardian.languageEditable ? language : undefined,
        spokenLanguage: spokenLanguage || '',
        email: guardian.emailData.showEditSection && guardian.emailData.canEditEmail ? email : undefined,
        receiveEmails: guardian.emailData.sendingManuallyDeactivated && user.admin === 0 ? undefined : receiveEmails,
        phoneNumber:
          guardian.phoneData.showEditSection && guardian.phoneData.canEditPhoneNumber ? phoneNumber : undefined,
        receiveTexts: guardian.phoneData.sendingManuallyDeactivated && user.admin === 0 ? undefined : receiveTexts,
        receiveTextsAsWhatsapp: textAsWhatsApp,
        audioOnly: sendTextToSpeech,
        receiveCalls: receiveCalls,
        sendAttendanceThroughEmail: guardian.attendance ? sendAttendanceEmails : undefined,
        sendAttendanceThroughText: guardian.attendance ? sendAttendanceTexts : undefined,
        preferredName: user.admin > 0 ? preferredName : undefined,
      })
      if (response === 'success') {
        reloadContacts()
      } else showEditErrors(response, setPhoneNumberInvalid, setEmailInvalid)
    } catch (e) {
      showAxiosErrorAsToast(e)
    } finally {
      setLoading(false)
    }
  }

  return (
    <GenericContact
      loading={loading}
      avatar={guardian.avatar}
      title={
        <>
          <SmartLink
            to={getDirectLink({
              [URLAction.OpenInboxTab]: 'sent',
              [URLAction.SetSearchUser]: { id: guardian.id, name: guardian.name },
              [URLAction.SetAllSchoolMessages]: user.admin > 0,
            })}
            className="no-underline"
          >
            <Badge badgeStyle={BadgeStyle.guardian} className="mr-auto !text-[15px]">
              {guardian.name}
              {guardian.preferredName && (
                <IconCircle
                  className="ml-1 text-warning"
                  size="xs"
                  tooltip={`Name from SIS: ${guardian.originalName}`}
                />
              )}
            </Badge>
          </SmartLink>

          {!editing && guardian.authUserCanSignInAs && (
            <SmartLink to={`/admin/login/${guardian.id}`}>
              <IconSignIn className="text-charcoal-text" tooltip={`Login as ${guardian.name}`} />
            </SmartLink>
          )}
          {editing ? (
            <>
              <Button logo small onClick={saveEdit}>
                Save
              </Button>
              <IconTimes
                tooltip="Cancel Editing"
                className="cursor-pointer opacity-50"
                onClick={() => setEditing(false)}
              />
            </>
          ) : guardian.editable ? (
            <IconEdit className="cursor-pointer text-charcoal-text" tooltip="Edit Guardian" onClick={edit} />
          ) : null}
        </>
      }
    >
      <div className="flex items-center gap-2">
        {!editing ? (
          <LanguageRow language={language} spokenLanguage={spokenLanguage} languageGlobe={guardian.languageGlobe} />
        ) : (
          <LanguageEdit
            language={language}
            setLanguage={setLanguage}
            spokenLanguage={spokenLanguage}
            setSpokenLanguage={setSpokenLanguage}
            canEditLanguage={guardian.languageEditable}
          />
        )}
      </div>
      <Divider className="-mx-3 !w-auto" />
      {editing ? (
        <EmailEdit
          emailData={guardian.emailData}
          user={user}
          email={email}
          setEmail={setEmail}
          receiveEmails={receiveEmails}
          setReceiveEmails={setReceiveEmails}
          invalid={emailInvalid}
        />
      ) : (
        <EmailRow
          contactName={guardian.name}
          contactID={guardian.id}
          emailData={guardian.emailData}
          sendEmailMessageLink={getComposeLink({ students: student.id, individuals: guardian.id, type: 'email' })}
          setLoading={setLoading}
          reloadContacts={reloadContacts}
        />
      )}
      {editing ? (
        <PhoneEdit
          phoneData={guardian.phoneData}
          user={user}
          phoneNumber={phoneNumber}
          invalid={phoneNumberInvalid}
          setPhoneNumber={setPhoneNumber}
          receiveTexts={receiveTexts}
          setReceiveTexts={setReceiveTexts}
          textAsWhatsApp={textAsWhatsApp}
          setTextAsWhatsApp={setTextAsWhatsApp}
          showSendTextToSpeech={
            languages.find(language)?.canTextToSpeech || languages.find(spokenLanguage)?.canTextToSpeech || false
          }
          sendTextToSpeech={sendTextToSpeech}
          setSendTextToSpeech={setSendTextToSpeech}
          receiveCalls={receiveCalls}
          setReceiveCalls={setReceiveCalls}
        />
      ) : (
        <PhoneRow
          contactName={guardian.name}
          contactID={guardian.id}
          phoneData={guardian.phoneData}
          sendTextMessageLink={getComposeLink({ students: student.id, individuals: guardian.id, type: 'text' })}
          setLoading={setLoading}
          reloadContacts={reloadContacts}
        />
      )}
      {editing && guardian.attendance && (
        <AttendanceMethodsEdit
          sendAttendanceEmails={sendAttendanceEmails}
          setSendAttendanceEmails={setSendAttendanceEmails}
          sendAttendanceTexts={sendAttendanceTexts}
          setSendAttendanceTexts={setSendAttendanceTexts}
        />
      )}
      {editing && user.admin > 0 && (
        <div className="flex flex-col gap-1">
          <div className="text-sm text-slate-400">Preferred Name</div>
          <TextInput value={preferredName} onChange={setPreferredName} blue />
        </div>
      )}
      <UsageRow data={guardian.usage} deliveryStatusID={user.systemAdministrator ? guardian.id : undefined} />
    </GenericContact>
  )
}

const AttendanceMethodsEdit: React.FC<{
  sendAttendanceEmails: boolean
  sendAttendanceTexts: boolean
  setSendAttendanceEmails: React.Dispatch<React.SetStateAction<boolean>>
  setSendAttendanceTexts: React.Dispatch<React.SetStateAction<boolean>>
}> = ({ sendAttendanceEmails, sendAttendanceTexts, setSendAttendanceEmails, setSendAttendanceTexts }) => {
  const [open, setOpen] = React.useState(false)
  const count = (sendAttendanceEmails ? 1 : 0) + (sendAttendanceTexts ? 1 : 0)
  return (
    <div className="flex flex-col gap-1">
      <div className="cursor-pointer select-none text-sm text-slate-400" onClick={() => setOpen((o) => !o)}>
        {open ? '-' : '+'}&nbsp; Attendance Methods {count}/2
      </div>
      {open && (
        <>
          <LabeledCheckbox
            checked={sendAttendanceEmails}
            onChecked={setSendAttendanceEmails}
            label="Send attendance through email"
          />
          <LabeledCheckbox
            checked={sendAttendanceTexts}
            onChecked={setSendAttendanceTexts}
            label="Send attendance through text"
          />
        </>
      )}
    </div>
  )
}
